// A placement's public link as its card shows it: the full URL to copy or
// open and, for a kiosk, the QR code to print. The public survey route
// (/s/*) is served by the admin's own origin — there is no separate public
// host to configure. New link (the token rotate) lives in the card's danger
// strip, not here.

import { CopyButton } from "@pej/kore/components";
import QRCode from "qrcode";
import { useEffect, useState } from "react";
import type { Placement } from "@/api/types";

export function PlacementLink({ placement, showQr }: { placement: Placement; showQr: boolean }) {
    const fullUrl = `${window.location.origin}${placement.publicPath}`;
    const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);

    // QRCode.toDataURL is async — render through state + effect, and
    // regenerate whenever the URL it encodes changes (a new link).
    useEffect(() => {
        if (!showQr) return;
        let cancelled = false;
        QRCode.toDataURL(fullUrl, { errorCorrectionLevel: "M", width: 240 }).then(
            (dataUrl) => {
                if (!cancelled) setQrDataUrl(dataUrl);
            },
            () => {
                if (!cancelled) setQrDataUrl(null);
            },
        );
        return () => {
            cancelled = true;
        };
    }, [fullUrl, showQr]);

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2">
                <code className="flex-1 truncate rounded-kore-md border border-kore-border-soft bg-kore-bg px-3 py-2 text-sm">
                    {fullUrl}
                </code>
                <CopyButton value={fullUrl} label="Copy public URL" />
                <a
                    href={fullUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm font-medium text-kore-accent-text underline-offset-4 hover:underline"
                >
                    Open
                </a>
            </div>
            {showQr && (
                <div className="flex items-center gap-4">
                    <img
                        src={qrDataUrl ?? undefined}
                        alt={`QR code for ${placement.name}`}
                        width={160}
                        height={160}
                        className="rounded-kore-md border border-kore-border-soft"
                    />
                    <p className="text-sm text-kore-text-muted">
                        Print this or scan it to open the placement's link directly.
                    </p>
                </div>
            )}
        </div>
    );
}
